import { Type, type Static } from "@sinclair/typebox";
import type { KnowledgeBuildExecutionV1, KnowledgeBuildProfileV1 } from "./knowledge-engine.ts";
import type { RetrievalExecutionConfig } from "./knowledge-retrieval.ts";

export const KNOWLEDGE_PROMPT_KINDS = [
  "semantic_boundary_selection",
  "business_view_extraction",
  "statement_leaf_resolver",
  "statement_parent_resolver",
  "parent_definition_generator",
  "knowledge_qa_reflection"
] as const;

export const KNOWLEDGE_BUILD_PROMPT_KINDS = [
  "semantic_boundary_selection",
  "business_view_extraction",
  "statement_leaf_resolver",
  "statement_parent_resolver",
  "parent_definition_generator"
] as const;

export type KnowledgePromptKind = (typeof KNOWLEDGE_PROMPT_KINDS)[number];
export type KnowledgeBuildPromptKind = (typeof KNOWLEDGE_BUILD_PROMPT_KINDS)[number];

export interface KnowledgeBuildConfigurationV1 {
  profile: KnowledgeBuildProfileV1;
  execution: KnowledgeBuildExecutionV1;
}

export type KnowledgeSourceFileExtensionV1 = "pdf" | "docx" | "md";

export interface KnowledgeSourceFilterV1 {
  rootPath: string;
  includeFileExtensions: KnowledgeSourceFileExtensionV1[];
}

export interface KnowledgeBaseConfigurationDocumentV1 {
  schemaVersion: "1.0";
  knowledgeBaseId: string;
  sourceFilter: KnowledgeSourceFilterV1;
  build: KnowledgeBuildConfigurationV1;
  retrieval: RetrievalExecutionConfig;
}

export interface KnowledgeBaseConfigurationRecordV1 {
  schemaVersion: "1.0";
  knowledgeBaseId: string;
  document: KnowledgeBaseConfigurationDocumentV1;
  documentFingerprint: string;
  generation: number;
  updatedBy: string;
  updatedAt: string;
}

const BuildPromptKindSchema = Type.Union(
  KNOWLEDGE_BUILD_PROMPT_KINDS.map((kind) => Type.Literal(kind))
);

const PromptPreviewParametersSchema = Type.Object(
  {
    maxOutputTokens: Type.Integer({ minimum: 1, maximum: 32_768 }),
    temperature: Type.Number({ minimum: 0, maximum: 1 }),
    maxAttempts: Type.Integer({ minimum: 1, maximum: 10 })
  },
  { additionalProperties: false }
);

export const KnowledgeBuildPromptPreviewInputSchema = Type.Object(
  {
    schemaVersion: Type.Literal("1.0"),
    promptKind: BuildPromptKindSchema,
    operatorInstructions: Type.String({ maxLength: 16_000 }),
    parameters: PromptPreviewParametersSchema
  },
  { additionalProperties: false }
);

export type KnowledgeBuildPromptPreviewInputV1 = Static<
  typeof KnowledgeBuildPromptPreviewInputSchema
>;

export const KnowledgeBuildPromptGenerationInputSchema = Type.Object(
  {
    schemaVersion: Type.Literal("1.0"),
    knowledgeBaseName: Type.String({ minLength: 1, maxLength: 256 }),
    knowledgeBaseDescription: Type.String({ minLength: 1, maxLength: 4_096 })
  },
  { additionalProperties: false }
);

export type KnowledgeBuildPromptGenerationInputV1 = Static<
  typeof KnowledgeBuildPromptGenerationInputSchema
>;

const GeneratedOperatorInstructionsSchema = Type.String({
  minLength: 120,
  maxLength: 4_000
});

export const KnowledgeBuildPromptSetSchema = Type.Object(
  {
    semanticBoundaryOperatorInstructions: GeneratedOperatorInstructionsSchema,
    businessViewOperatorInstructions: GeneratedOperatorInstructionsSchema,
    leafConceptResolutionOperatorInstructions: GeneratedOperatorInstructionsSchema,
    parentResolutionOperatorInstructions: GeneratedOperatorInstructionsSchema
  },
  { additionalProperties: false }
);

export type KnowledgeBuildPromptSetV1 = Static<typeof KnowledgeBuildPromptSetSchema>;

const GeneratedEmbeddingInstructionBodySchema = Type.String({
  minLength: 60,
  maxLength: 2_000
});

export const KnowledgeBuildEmbeddingInstructionBodiesSchema = Type.Object(
  {
    rawUnitQuery: GeneratedEmbeddingInstructionBodySchema,
    statementQuery: GeneratedEmbeddingInstructionBodySchema,
    statementClustering: GeneratedEmbeddingInstructionBodySchema,
    queryAliasQuery: GeneratedEmbeddingInstructionBodySchema,
    conceptNavigationQuery: GeneratedEmbeddingInstructionBodySchema,
    conceptNavigationClustering: GeneratedEmbeddingInstructionBodySchema
  },
  { additionalProperties: false }
);

export type KnowledgeBuildEmbeddingInstructionBodiesV1 = Static<
  typeof KnowledgeBuildEmbeddingInstructionBodiesSchema
>;

export const KnowledgeBuildPromptGenerationSchema = Type.Object(
  {
    schemaVersion: Type.Literal("1.0"),
    prompts: KnowledgeBuildPromptSetSchema,
    embeddingInstructionBodies: KnowledgeBuildEmbeddingInstructionBodiesSchema
  },
  { additionalProperties: false }
);

export type KnowledgeBuildPromptGenerationV1 = Static<typeof KnowledgeBuildPromptGenerationSchema>;

export interface KnowledgePromptPreviewV1 {
  schemaVersion: "1.0";
  promptKind: KnowledgePromptKind;
  owner: "ingestion-worker" | "platform-api";
  modelRef: string;
  baseTemplate: string;
  baseTemplateVersion: string;
  operatorInstructions: string;
  placeholders: Array<{ name: string; description: string; syntheticValue: string }>;
  schemaVersionId: string;
  schemaSummary: string;
  guardrails: string[];
  effectivePrompt: string;
  inputHash: string;
  promptHash: string;
  toolSchemaHash: string;
}
